import {
  Activity,
  BadgeCheck,
  BookUser,
  CalendarDays,
  ClipboardList,
  Hourglass,
  IndianRupee,
  LayoutDashboard,
  LayoutGrid,
  Mail,
  MessageSquare,
  Phone,
  Phone as PhoneIcon,
  ScrollText,
  ShieldCheck,
  UserCheck,
  UserCircle,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_META = {
  ca:          { label: 'CA',          color: 'bg-blue-100 text-blue-700',      blurb: 'Chartered Accountant — handles assigned orders and client work.' },
  admin:       { label: 'Admin',       color: 'bg-purple-100 text-purple-700',  blurb: 'Admin — manages orders, users, contacts and the members directory.' },
  super_admin: { label: 'Super Admin', color: 'bg-[#6C63FF]/15 text-[#6C63FF]', blurb: 'Super Admin — full access including payments, logs and team management.' },
};

// Mirrors AdminSidebar's NAV_ITEMS so the profile reflects exactly what each
// role can reach. `roles: null` means every signed-in role.
const MODULES = [
  { icon: LayoutDashboard, label: 'Dashboard',         path: '/admin',              roles: ['admin', 'super_admin'] },
  { icon: ClipboardList,   label: 'Orders',            path: '/admin/orders',        roles: null },
  { icon: PhoneIcon,       label: 'Call Requests',     path: '/admin/call-requests', roles: null },
  { icon: Users,           label: 'Users',             path: '/admin/users',         roles: ['admin', 'super_admin'] },
  { icon: MessageSquare,   label: 'Contacts',          path: '/admin/contacts',      roles: ['admin', 'super_admin'] },
  { icon: BookUser,        label: 'Members Directory', path: '/admin/members',       roles: ['admin', 'super_admin'] },
  { icon: UserCheck,       label: 'CA Profiles',       path: '/admin/ca-profiles',   roles: ['admin', 'super_admin'] },
  { icon: Hourglass,       label: 'Stalled Users',     path: '/admin/stalled-users', roles: ['super_admin'] },
  { icon: IndianRupee,     label: 'Payments',          path: '/admin/payments',      roles: ['super_admin'] },
  { icon: ScrollText,      label: 'Activity Logs',     path: '/admin/logs',          roles: ['super_admin'] },
  { icon: Activity,        label: 'Customer Logs',     path: '/admin/customer-logs', roles: ['super_admin'] },
  { icon: ShieldCheck,     label: 'Team',              path: '/admin/team',          roles: ['super_admin'] },
];

// Mirrors AdminSidebar: profile images are `/uploads/...` relative paths or
// absolute URLs. Relative paths need the API origin prefixed.
const resolveAvatarUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (s.startsWith('/')) return `${base}${s}`;
  return `${base}/uploads/${s}`;
};

function HeroAvatar({ user }) {
  const [failed, setFailed] = useState(false);
  const src = resolveAvatarUrl(user?.profile_image);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={user?.name || 'profile'}
        onError={() => setFailed(true)}
        className="w-20 h-20 rounded-2xl object-cover border border-white/25 shadow-lg flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white text-2xl font-bold font-heading shadow-lg flex-shrink-0">
      {user?.name?.[0]?.toUpperCase() || 'A'}
    </div>
  );
}

function DetailCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3.5 rounded-xl border border-[#EEEDF7] bg-[#FBFBFE] px-4 py-3.5 transition-colors hover:border-[#6C63FF]/30 hover:bg-white">
      <div className="bg-[#6C63FF]/10 rounded-lg p-2.5 flex-shrink-0">
        <Icon className="h-4 w-4 text-[#6C63FF]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-body font-semibold tracking-wide uppercase text-[#9CA3AF]">
          {label}
        </p>
        <p className="text-sm font-body font-medium text-[#1A1A2E] mt-0.5 break-words">
          {value || <span className="text-[#C4C2D4] font-normal">Not provided</span>}
        </p>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();

  if (!user) return null;

  const roleMeta = ROLE_META[user.role] || {
    label: user.role,
    color: 'bg-gray-100 text-gray-600',
    blurb: '',
  };

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const isActive = user.is_active !== 0;

  const accessibleModules = MODULES.filter(
    (m) => m.roles === null || m.roles.includes(user.role)
  );

  const heroStats = [
    { icon: ShieldCheck,  label: 'Role',        value: roleMeta.label },
    { icon: LayoutGrid,   label: 'Sections',    value: `${accessibleModules.length} accessible` },
    { icon: CalendarDays, label: 'Member since', value: memberSince },
  ];

  return (
    <div className="space-y-5">
      {/* ─── Hero header (matches Orders) ──────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#A78BFA]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/15 rounded-full blur-3xl" />

        <div className="relative flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <HeroAvatar user={user} />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight truncate">
                {user.name || 'Admin'}
              </h1>
              <p className="text-sm text-white/70 font-body mt-0.5 truncate">{user.email}</p>
              <div className="flex items-center gap-2 mt-2.5">
                <span className="inline-block text-[11px] font-body font-semibold px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/15 text-white">
                  {roleMeta.label}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-body font-semibold px-2.5 py-0.5 rounded-full border ${isActive ? 'bg-emerald-400/15 border-emerald-300/30 text-emerald-200' : 'bg-red-400/15 border-red-300/30 text-red-200'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-300 animate-pulse' : 'bg-red-300'}`} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className="flex items-center gap-3 flex-wrap">
            {heroStats.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-white/[0.07] backdrop-blur-sm border border-white/15"
              >
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <s.icon className="h-4 w-4 text-[#A5B4FC]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-body uppercase tracking-wide text-white/55">{s.label}</p>
                  <p className="text-sm font-heading font-semibold text-white truncate">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Details + role blurb ──────────────────────────────────────── */}
      <div className="grid lg:grid-cols-3 gap-5 items-start">
        {/* Account details */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-5">
            <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">Account Details</h3>
            <div className="flex-1 h-px bg-[#F0EFF8]" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <DetailCard icon={UserCircle} label="Full Name" value={user.name} />
            <DetailCard icon={Mail} label="Email Address" value={user.email} />
            <DetailCard icon={Phone} label="Phone" value={user.phone} />
            <DetailCard icon={BadgeCheck} label="Role" value={roleMeta.label} />
            <div className="sm:col-span-2">
              <DetailCard icon={CalendarDays} label="Member Since" value={memberSince} />
            </div>
          </div>

          <p className="text-xs text-[#9CA3AF] font-body mt-6 pt-5 border-t border-[#F0EFF8]">
            Need to update your details or password? Contact a Super Admin.
          </p>
        </div>

        {/* Role card */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">Your Role</h3>
            <div className="flex-1 h-px bg-[#F0EFF8]" />
          </div>
          <div className="bg-gradient-to-br from-[#6C63FF]/[0.08] to-[#8B7EFF]/[0.04] border border-[#6C63FF]/15 rounded-xl p-4">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#6C63FF] flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <span className={`inline-block text-xs font-body font-semibold px-2.5 py-0.5 rounded-full ${roleMeta.color}`}>
                {roleMeta.label}
              </span>
            </div>
            <p className="text-sm text-[#4A4A60] font-body leading-relaxed">{roleMeta.blurb}</p>
          </div>
        </div>
      </div>

      {/* ─── Workspace access ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-6 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">Workspace Access</h3>
          <div className="flex-1 h-px bg-[#F0EFF8]" />
        </div>
        <p className="text-xs text-[#9CA3AF] font-body mb-5">
          Sections available to your <span className="font-semibold text-[#6B7280]">{roleMeta.label}</span> role — click to jump in.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {accessibleModules.map((m) => (
            <Link
              key={m.path}
              to={m.path}
              className="group flex items-center gap-3 rounded-xl border border-[#EEEDF7] bg-[#FBFBFE] px-4 py-3 transition-all hover:border-[#6C63FF]/40 hover:bg-white hover:shadow-sm"
            >
              <div className="bg-[#6C63FF]/10 rounded-lg p-2 flex-shrink-0 transition-colors group-hover:bg-[#6C63FF]">
                <m.icon className="h-4 w-4 text-[#6C63FF] transition-colors group-hover:text-white" />
              </div>
              <span className="text-sm font-body font-medium text-[#1A1A2E] truncate">
                {m.label}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
